<script setup>
    import { computed, Transition, ref, reactive, onMounted, watch, nextTick } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { useI18n } from 'vue-i18n'

    import { useStageStore } from '@/stores/stage';
    import { useConfigStore } from '@/stores/config'
    import ResultViewModal from '../components/ResultViewModal.vue'

    import PathNodes from "@/components/paths/PathNodes.vue"
    import { SvgPanZoom } from "vue-svg-pan-zoom"

    import ResultsView from "@/views/ResultsView.vue"
    import SummaryView from "./SummaryView.vue";

    import InputFields from '@/components/InputFields.vue';
    import SurveryProgress from '../components/SurveryProgress.vue';
    import FormStageNavigation from '../components/FormStageNavigation.vue';
    import FormPathDetails from '../components/FormPathDetails.vue';
    import Points from '../components/Points.vue';
    import ToggleMapBtn from '@/components/ToggleMapBtn.vue';
    import PasswordForm from '@/components/forms/PasswordForm.vue';
    import PageNotFound from '@/views/PageNotFound.vue'

    const route = useRoute();
    const router = useRouter()
    const formStorage = useStageStore();
    const currentLang = formStorage.language;
    const { t } = useI18n()

    const initialLoad = ref(true)
    const modalVisible = ref(false)

    const surveyStage = computed(() => {
        return formStorage.getStageById(route.params.id)[0];
    })

    // **************** //
    // SVG map
    // **************** //
    const configStore = useConfigStore()
    const viewBox = reactive({})
    const svgElement = ref(null)

    // Registe SVG PAN ZOOM element
    const registerSvgPanZoom = (svgpanzoom) => {
        // console.log('svgpanzoom', svgpanzoom)
        svgElement.value = svgpanzoom
        svgElement.value.updateBBox()
        svgElement.value.pan({x: -563, y: -248})
    }

    const getCurrentZoom = (newScale) => {
        configStore.setNewZoom(newScale)
        // console.log(newScale)
    }

    const getCurrentPan = (newPan) => {
        // console.log('getcurrentpan ', newPan)
        configStore.setNewPan(newPan)
    }

    const showModal = () => {
      formStorage.managePointsByPathType()
      modalVisible.value = true
    }
    const modalHidden = () => {
      modalVisible.value = false
    }

    const mappi = ref(null)

    const centerMapActiveByStage = () => {
        setTimeout(() => {
            const mapContainerHeight = mappi.value.getBoundingClientRect().height
            const mapContainerWidth = mappi.value.getBoundingClientRect().width

            const panX = mapContainerWidth / 2 - surveyStage.value.node.location.cx
            const panY = mapContainerHeight / 2 - surveyStage.value.node.location.cy

            if (initialLoad.value) {
                svgElement.value.zoom(0.75);
                initialLoad.value = false;
            }

            svgElement.value.pan({ x: panX, y:panY})
        }, 200);
    }

    const addTextBackgroundElement = (gElement) => {
        const text = gElement.querySelector('text')
        const box = text.getBBox();

        const padding = 0;
        
        let rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        rect.classList.add('text-background-element')
        rect.setAttribute("x", box.x - padding);
        rect.setAttribute("y", box.y - padding);
        rect.setAttribute("width", box.width + (padding * 2));
        rect.setAttribute("height", box.height + (padding * 2));
        rect.setAttribute("rx", 2); // Corner radius

        const styleText = "fill:#ffffff99;";
        rect.style = styleText;

        gElement.insertBefore(rect, text);
    }

    const showQuestion = () => {
        // mapToggle
        formStorage.showMap = false;
        document.querySelector('#app_public form').focus();
    }

    onMounted(() => {
        if(formStorage.showMap){
            initializeStage()
        }

        formStorage.setCurrentStage(route.params.id)
    })

    // Watch for isPanning value and
    // enable or disable based on choices
    watch(() => configStore.getIsPanning, () => {
        if (configStore.getIsPanning) {
            svgElement.value.enablePan()
        } else {
            svgElement.value.disablePan()
        }
    })

    watch(() => formStorage.showMap, () => {
        if(formStorage.showMap){
            centerMapActiveByStage()

            setTimeout(() => {
                if (mappi.value) {
                    // Add visual helper boxes behind text elements in map view
                    const mapNodes = mappi.value.querySelectorAll('.map-container .map-nodes > g')
                    mapNodes.forEach((gElement) => {
                        addTextBackgroundElement(gElement)
                    })
                }
            }, 200)
        }

        setFormVisibility(formStorage.showMap)
    })

    const setFormVisibility = (showState) => {
        const form = document.querySelector(`#survey-form_${formStorage.getNameSpace}`);
        const formRow = document.querySelector(`#survey-form_${formStorage.getNameSpace} > .row`);
        let tabindex = showState ? '-1' : '0';
        let display = showState ? 'none' : 'flex';

        setTimeout(() => {
            form.setAttribute('tabindex', tabindex);
            formRow.style.display = display;
        }, 200);
    }

    const initializeStage = () => {
        Object.assign(viewBox, configStore.getViewbox)
        if (Object.keys(viewBox.pan.x !== null)) {
            // console.log('viewBox', viewBox)
            svgElement.value.pan({ x: viewBox.pan.x, y:viewBox.pan.y})
        }

        if (viewBox.zoom !== null) {
            svgElement.value.zoom(viewBox.zoom)
        }
    }

    const getStateOfAnswers = () => {
      const acceptableAnswers = formStorage.getAnswerData.filter((answer) => answer.answer !== 'doneByDefault' && answer.answer !== 'blockedByRelations'
          && answer.answer !== 'clicked')
      return acceptableAnswers.length > 0;
    }

    // Calculate total points
    watch(route, (from, to) => {
        nextTick(() => {
            formStorage.calculateTotalPoints();
            formStorage.setCurrentStage(route.params.id)

            if (!formStorage.previewMode) {
                // Prevent view jumping to bottom part of page after question selection
                document.querySelector(`#survey-form_${formStorage.getNameSpace}`).focus();
            }
        })
    })

    /* page refres alert - Changes you made may not be saved */
    if (!formStorage.previewMode) {
        window.addEventListener('beforeunload', function(event) {
            event.returnValue = 'Write something'
        })
    }
</script>

<template>
    <div class="survey-top-container">
        <div class="survey-title-container">
            <h1 
                v-if="formStorage?.stagesData?.title" 
                :id="`survey-title-${formStorage.stagesData.surveyId}`"
                class="survey-title"
            >
                {{ formStorage.stagesData.title[currentLang] }}
            </h1>
        </div>
    </div>

    <div v-if="formStorage.showMap" class="back-to-survey-container">
        <div class="back-to-survey">
            <button 
                class="btn btn-primary back-element"
                v-on:click="showQuestion()"
            >
                <i class="back-element-return-icon icon-arrow-left" aria-hidden="true"></i>
                <span>{{ t('button.return-back') }}</span>
            </button>
        </div>
    </div>

    <PasswordForm v-if="formStorage.showPasswordForm" />

    <template v-else-if="formStorage.surveyStageExists(route.params.id)">
        <form
            :id="`survey-form_${formStorage.getNameSpace}`"
            @submit.prevent=""
            :aria-labelledby="`survey-title-${formStorage.stagesData.surveyId}`"
            :class="formStorage.showMap ? 'slide-off' : ''"
            tabindex="0"
        >
            <SurveryProgress
                v-if="getStateOfAnswers() && surveyStage?.ancestors?.length !== 0" 
                :stage="surveyStage" 
            />

            <div class="row">
                <div class="col-md-9">
                    <h2 v-if="surveyStage?.title" id="stage-heading">{{ surveyStage.title[currentLang] }}</h2>
                    <FormPathDetails
                        :stage="surveyStage"
                        :route="route.params.id" 
                    />

                    <div class="form-content mb-4">
                        <div v-if="surveyStage?.description" v-html="surveyStage.description[currentLang]" class="description"></div>
                        <img v-if="surveyStage.image.url !== ''" :src="surveyStage.image.url" :alt="surveyStage.image.alt[currentLang]" />
                    </div>

                    <InputFields />

                    <FormStageNavigation
                        v-if="formStorage.getAllAnswersByStageId(route.params.id)?.answer.length > 0"
                        :stage="surveyStage"
                        :route="route.params.id"
                    />
                </div>

                <div class="col-md-3 text-align-right">
                    <Points :stage="surveyStage" />
                    <ToggleMapBtn />
                    <div v-if="getStateOfAnswers()" style="display: flex; justify-content: center">
                      <button
                          class="btn btn-primary publish"
                          @click="showModal"
                          v-if="formStorage.answersData.length > 1"
                          style="margin-right: 0 !important; margin-top: 0.5rem"
                      >
                        {{ t('actions.check-answers') }}
                      </button>
                    </div>
                </div>
            </div>

        </form>

        <Transition name="fade">
            <div class="survey-map opacity-0" ref="mappi" v-if="formStorage.showMap">
                <SvgPanZoom
                    style=" border: 1px solid #d5d6d7;"
                    @created="registerSvgPanZoom"
                    @onPan="getCurrentPan"
                    @onZoom="getCurrentZoom"
                >
                    <PathNodes />
                </SvgPanZoom>
            </div>
        </Transition>
    </template>

    <ResultsView v-else-if="formStorage.calculateSurveyProgressInPercents() > 0" />

    <template v-else>
        <PageNotFound />
    </template>

    <ResultViewModal
        :visible="modalVisible"
        @modalHidden="modalHidden"
    >
      <template #modalHeader>
        <h2>{{ $t('table.answers') }}</h2>
      </template>
      <template #modalContent>
        <SummaryView />
      </template>
      <template #modalFooter>
        <button @click="modalHidden" class="btn btn-primary publish">
          {{  $t('modal.close') }}
        </button>
      </template>
    </ResultViewModal>


</template>
